import { modelState } from "@/utils/modelState";

export namespace RatesRateDiscountOverrides {
    export const RateDiscountOverride = Symbol("RateDiscountOverride");
    export interface RateDiscountOverride {
        [RateDiscountOverride]: boolean;
    }

    export interface RateDiscountOverrideDto {
        $type: string;
        value: RateDiscountOverrideValueDto;
    }

    export interface RateDiscountOverrideValueDto {
        //
    }

    export function serializeRateDiscountOverride(action: RateDiscountOverride): RateDiscountOverrideDto {
        if (!action) {
            return null;
        }

        for (let rateDiscountOverride of rateDiscountOverrides) {
            if (rateDiscountOverride.isInstanceOf(action)) {
                return {
                    $type: rateDiscountOverride.key,
                    value: rateDiscountOverride.serialize(action)
                };
            }
        }

        throw new Error("Invalid status type");
    }

    export function deserializeRateDiscountOverride(dto: RateDiscountOverrideDto): RateDiscountOverride {
        if (!dto) {
            return null;
        }

        for (let rateDiscountOverride of rateDiscountOverrides) {
            if (rateDiscountOverride.key === dto.$type) {
                return rateDiscountOverride.deserialize(dto.value);
            }
        }

        throw new Error("Invalid status type");
    }

    export function getRateDiscountOverrideName(status: RateDiscountOverride): string {

        for (let rateDiscountOverride of rateDiscountOverrides) {
            if (rateDiscountOverride.isInstanceOf(status)) {
                return rateDiscountOverride.name;
            }
        }

        throw new Error("Invalid status type");
    }

    export function createRateDiscountOverride(key: string): RateDiscountOverride {

        for (let rateDiscountOverride of rateDiscountOverrides) {
            if (rateDiscountOverride.key === key) {
                return rateDiscountOverride.create();
            }
        }

        throw new Error("Invalid rateDiscountOverride key");
    }



    export const ExistingRateDiscountOverride = Symbol("ExistingRateDiscountOverride");
    export interface ExistingRateDiscountOverride extends RateDiscountOverride {
        [ExistingRateDiscountOverride]: boolean;
        RateDiscountId: string;
        RateDiscountVersion: number;
    }

    export function isExistingRateDiscountOverride(val: RateDiscountOverride): val is ExistingRateDiscountOverride {
        return val && (val as ExistingRateDiscountOverride)[ExistingRateDiscountOverride] === true;
    }

    export interface ExistingRateDiscountOverrideValueDto extends RateDiscountOverrideValueDto {
        rateDiscountId: string;
        rateDiscountVersion: number;
    }

    export const ExistingRateDiscountOverrideDefinition: RateDiscountOverrideDefinitionT<ExistingRateDiscountOverride, ExistingRateDiscountOverrideValueDto> =
    {
        key: "Existing",
        name: "Existing",
        isInstanceOf: (rateDiscountOverride: RateDiscountOverride) => isExistingRateDiscountOverride(rateDiscountOverride),
        create: (): ExistingRateDiscountOverride => {
            return {
                [RateDiscountOverride]: true,
                [ExistingRateDiscountOverride]: true,
                RateDiscountId: null,
                RateDiscountVersion: null
            };
        },
        serialize: (rateDiscountOverride: ExistingRateDiscountOverride): ExistingRateDiscountOverrideValueDto => {
            return {
                rateDiscountId: rateDiscountOverride.RateDiscountId,
                rateDiscountVersion: rateDiscountOverride.RateDiscountVersion
            };
        },
        deserialize: function (this: RateDiscountOverrideDefinition, dto: ExistingRateDiscountOverrideValueDto) {
            const existing = this.create() as ExistingRateDiscountOverride;
            existing.RateDiscountId = dto.rateDiscountId;
            existing.RateDiscountVersion = dto.rateDiscountVersion;
            return existing;
        }
    };


    export const CustomRateDiscountOverride = Symbol("CustomRateDiscountOverride");
    export interface CustomRateDiscountOverride extends RateDiscountOverride {
        [CustomRateDiscountOverride]: boolean;
        Name: modelState<string>;
        Amount: modelState<number>;
    }

    export function isCustomRateDiscountOverride(val: RateDiscountOverride): val is CustomRateDiscountOverride {
        return val && (val as CustomRateDiscountOverride)[CustomRateDiscountOverride] === true;
    }

    export interface CustomRateDiscountOverrideValueDto extends RateDiscountOverrideValueDto {
        name: string;
        amount: number;
    }

    export const CustomRateDiscountOverrideDefinition: RateDiscountOverrideDefinition = {
        key: "Custom",
        name: "Custom",
        isInstanceOf: (rateDiscountOverride: RateDiscountOverride) => isCustomRateDiscountOverride(rateDiscountOverride),
        create: (): CustomRateDiscountOverride => {
            return {
                [RateDiscountOverride]: true,
                [CustomRateDiscountOverride]: true,
                Name: modelState(""),
                Amount: modelState(0)
            };
        },
        serialize: (rateDiscountOverride: CustomRateDiscountOverride): CustomRateDiscountOverrideValueDto => {
            return {
                name: rateDiscountOverride.Name.value,
                amount: rateDiscountOverride.Amount.value
            };
        },
        deserialize: function (this: RateDiscountOverrideDefinition, dto: CustomRateDiscountOverrideValueDto) {
            const custom = this.create() as CustomRateDiscountOverride;

            custom.Name.setValue(dto.name)
            custom.Name.commit();

            custom.Amount.setValue(dto.amount)
            custom.Amount.commit();

            return custom;
        }
    };


    interface RateDiscountOverrideDefinition {
        key: string;
        name: string;
        isInstanceOf: (rateDiscountOverride: RateDiscountOverride) => boolean;
        create: () => RateDiscountOverride;
        serialize: (rateDiscountOverride: RateDiscountOverride) => RateDiscountOverrideValueDto;
        deserialize: (dto: RateDiscountOverrideValueDto) => RateDiscountOverride;
    }

    interface RateDiscountOverrideDefinitionT<T, TDto> extends Omit<RateDiscountOverrideDefinition, 'create' | 'serialize' | 'deserialize'> {
        create: () => T;
        serialize: (rateDiscountOverride: T) => TDto;
        deserialize: (dto: TDto) => T;
    }

    const rateDiscountOverrides: RateDiscountOverrideDefinition[] = [
        ExistingRateDiscountOverrideDefinition,
        CustomRateDiscountOverrideDefinition
    ]
}
